import { TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { MathUtils } from "html-living-framework";
import { Ease } from "../core/Animatable";
import { IPoint, PointUtil } from "../core/Geom";
import { Callback } from "../core/Reflection";
import { Sprite } from "../core/Sprite";
import { AbstractCharacter } from "./AbstractCharacter";
import { Oscar } from "./Oscar";
import { TrashItem } from "./TrashItem";
import { TrashItemCollect } from "./TrashItemCollect";
import { TrashItemReturn } from "./TrashItemReturn";

export class OscarReturn extends Oscar {

    set collectedItems(pItems: TrashItem[]) {
        if (this._collectedItems.length === 0) {
            this._collectedItems.push(...pItems);
        }
        else {
            throw new Error("array is readonly, only use this set accessor when array is empty");
        }
    }

    public get loadedItem() { return this._loadedItem; }

    public readonly bubble = this.addChild(new Sprite());
    public readonly loadPoint = { x: 0, y: 0 };

    public minY: number = 0;

    public aimingAt: "left" | "center" | "right" | "none" = "none";

    protected readonly _itemsToCollect: TrashItemCollect[] = [];
    protected readonly _collectedItems: TrashItemReturn[] = [];
    protected _lastMousePos?: PIXI.Point;
    private _isLoading = false;
    private _loadedItem?: TrashItem;

    public onLayout() {
        super.onLayout();
        const bagFillAnimation = this.spine.state.setAnimation(this.tracks.BAG, "bag-reverse", false);
        bagFillAnimation.timeScale = 0;
        this.setState(OscarState.Normal);
        this.bubble.visible = false;
        this.bubble.interactive = true; // to block clicks

        this.onMouseMoved = this.onMouseMoved.bind(this);
        const interactionManager: PIXI.InteractionManager = HLF.Application.instance.renderer.plugins.interaction;
        interactionManager.addListener(HLF.Input.Events.POINTER_MOVE, this.onMouseMoved);
        interactionManager.addListener(HLF.Input.Events.POINTER_DOWN, this.onMouseMoved);

        this.spine.state.setAnimation(this.tracks.MOTOR, "vaccum-running", true);

        this.slimyClickTarget.interactive = true;
        this.slimyClickTarget.buttonMode = false;

        this.nozzleClickTarget.interactive = true;
        this.nozzleClickTarget.buttonMode = false;
    }

    public get nozzleWorldPosition(): PIXI.IPointData {
        const nozzleBone = this.spine.skeleton.findBone("nozzle");
        return this.toGlobal({
            x: nozzleBone.worldX,
            y: nozzleBone.worldY,
        });
    }

    public update(time: number) {
        super.update(time);

        if (this._lastMousePos && this.visible) {
            const hoseBone = this.spine.skeleton.findBone("right-arm-hose-target");
            const newBonePos = hoseBone.parent.worldToLocal(new PIXI.spine.core.Vector2(
                Math.max(this._lastMousePos.x, 1000),
                this._lastMousePos.y,
            ));
            const ik = this.spine.skeleton.findIkConstraint("right-arm");
            ik.stretch = false;
            hoseBone.data.x = newBonePos.x;
            hoseBone.data.y = newBonePos.y;

            const nozzleBone = this.spine.skeleton.findBone("nozzle");
            const nozToMouseDist = {
                x: this._lastMousePos.x - nozzleBone.worldX,
                y: nozzleBone.worldY - this._lastMousePos.y,
            };

            const nozRot = (Math.atan2(nozToMouseDist.x, nozToMouseDist.y) * PIXI.RAD_TO_DEG) + 90;

            const prevAimingAt = this.aimingAt;
            if (this._lastMousePos.y < -this.height * (3 / 4)) {
                if (this._lastMousePos.x < 480 && this._lastMousePos.x > 50) {
                    this.aimingAt = "left";
                }
                else if (this._lastMousePos.x >= 480 && this._lastMousePos.x < 960) {
                    this.aimingAt = "center";
                }
                else if (this._lastMousePos.x >= 960) {
                    this.aimingAt = "right";
                }
                else {
                    this.aimingAt = "none";
                }
            }
            else {
                this.aimingAt = "none";
            }

            PubSub.publishSync("oscar aim", this.aimingAt);

            if (this.aimingAt !== prevAimingAt) {
                let entry;

                switch (this.aimingAt) {
                    case "left":
                        entry = this.spine.state.setAnimation(this.tracks.ARMS, "hose-curve-up", false);
                        entry.animationEnd /= 2;
                        break;
                    case "center":
                    case "right":
                        if (prevAimingAt === "left") {
                            entry = this.spine.state.setAnimation(this.tracks.ARMS, "hose-curve-up", false);
                            entry.animationStart = entry.animationEnd / 2;
                        }
                        else if (prevAimingAt === "none") {
                            entry = this.spine.state.setAnimation(this.tracks.ARMS, "hose-curve-down", false);
                            entry.animationStart = entry.animationEnd / 2;
                        }
                        break;
                    case "none":
                        entry = this.spine.state.setAnimation(this.tracks.ARMS, "hose-curve-down", false);
                        entry.animationEnd /= 2;
                        break;
                }
            }
            nozzleBone.data.rotation = MathUtils.clamp(nozzleBone.parent.worldToLocalRotation(nozRot), -10, 0);
        }

    }

    public loadItem(cb?: Callback) {
        if (this._isLoading) {
            return false;
        }
        if (this._collectedItems.length === 0) {
            this.bubble.visible = false;
            return false;
        }
        this._isLoading = true;
        this.setState(OscarState.Cannon);

        const item = this._collectedItems.shift()!;
        this.addChild(item);
        item.alpha = 1;
        item.rotation = 0;
        const itemMaxSize = 200;
        item.width = Math.min(item.width * 4, itemMaxSize);
        item.height = Math.min(item.height * 4, itemMaxSize);
        item.scale.set(Math.min(item.scale.x, item.scale.y));
        item.x = this.loadPoint.x;
        item.y = this.loadPoint.y;

        this.bubble.visible = true;

        HLF.Application.instance.voiceover.playVO([0.3, item.voiceoverA], HLF.PlayMode.Append);

        item.scaleFrom(0, 0.4, Ease.backOut, () => {
            this._isLoading = false;
            this._loadedItem = item;
            if (cb) {
                cb();
            }
        });

        return true;
    }

    public throwItem() {
        const item = this._loadedItem;
        if (item) {
            this._loadedItem = undefined;
            this.setState(OscarState.Normal);
            const bagAnimation = this.spine.state.setAnimation(this.tracks.BAG, "bag-reverse", false);
            bagAnimation.time = (1 - ((this._collectedItems.length) / 6)) * bagAnimation.animationEnd;
            bagAnimation.animationEnd = (1 - ((this._collectedItems.length - 1) / 6)) * bagAnimation.animationEnd;

            const bulgeAnimation = this.spine.state.setAnimation(this.tracks.HOSE, "bulge-reverse", false);
            bulgeAnimation.timeScale = 1.25;

            this.spine.state.addAnimation(this.tracks.NOZZLE, "air-blow", false, bulgeAnimation.animationEnd * 0.75);
            this.spine.state.addEmptyAnimation(this.tracks.NOZZLE, 0, 0);

            const sound1 = HLF.Application.instance.audio.getAudioTrack("trash-blaster-blow", HLF.AudioCategory.SFX);
            const sound2 = HLF.Application.instance.audio.getAudioTrack("trash-blaster-blow-release-item", HLF.AudioCategory.SFX);
            sound1?.once(HLF.HowlerUtils.Events.END, () => {
                sound2?.play();
            });
            sound1?.play();
        }
        return item;
    }

    public catchItem(item: TrashItem) {
        this._loadedItem = item;
        this.setState(OscarState.Cannon);
        // HLF.PixiUtils.setParent(item, this.nozzle);
    }

    public wait() {
        this.setState(OscarState.Wait);
    }

    public destroy() {
        super.destroy();
        const interactionManager: PIXI.InteractionManager = HLF.Application.instance.renderer.plugins.interaction;
        interactionManager.removeListener(HLF.Input.Events.POINTER_MOVE, this.onMouseMoved);
        interactionManager.removeListener(HLF.Input.Events.POINTER_DOWN, this.onMouseMoved);
    }

    protected onMouseMoved(pData: PIXI.InteractionEvent) {
        this._lastMousePos = pData.data.getLocalPosition(this.spine);
    }

    private setState(state: OscarState) {
        // this.oscarNormal.visible = true;
        // this.nozzle.visible = true;
        // this.oscarCannon.visible = state === OscarState.Cannon;
        // this.oscarWait.visible = state === OscarState.Wait;
    }
}

enum OscarState {
    Normal,
    Cannon,
    Wait,
}
